import React, { createContext } from "react";

import { uniqBy } from "lodash";
import { useIntl } from "react-intl";

import { getUserExtendedAccess } from "Api/users/getUserExtendedAccess";
import client from "Libs/platform";
import { meSelector } from "Reducers/app/selectors";
import { useAppSelector } from "Store/hooks";

import { Docs } from "./type";

function useSafeDispatch(dispatch) {
  const mounted = React.useRef(false);

  React.useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return React.useCallback(
    (...args) => (mounted.current ? dispatch(...args) : void 0),
    [dispatch]
  );
}

function asyncReducer(state, action) {
  switch (action.type) {
    case "pending": {
      return { ...state, status: "pending", error: null };
    }
    case "resolved": {
      return { status: "resolved", data: action.data, error: null };
    }
    case "rejected": {
      return { status: "rejected", data: null, error: action.error };
    }
    default: {
      throw new Error("Unhandled action type");
    }
  }
}

export function useAsync(initialState) {
  const [state, unsafeDispatch] = React.useReducer(asyncReducer, {
    status: "idle",
    data: null,
    error: null,
    ...initialState
  });

  const dispatch = useSafeDispatch(unsafeDispatch);

  const { data, error, status } = state;

  const run = React.useCallback(
    promise => {
      dispatch({ type: "pending" });
      promise.then(
        data => {
          dispatch({ type: "resolved", data });
        },
        error => {
          dispatch({ type: "rejected", error });
        }
      );
    },
    [dispatch]
  );

  const setData = React.useCallback(
    data => dispatch({ type: "resolved", data }),
    [dispatch]
  );
  const setError = React.useCallback(
    error => dispatch({ type: "rejected", error }),
    [dispatch]
  );

  return {
    setData,
    setError,
    error,
    status,
    data,
    run
  };
}

export function useCommandResources() {
  const { run, data, error, status } = useAsync({});
  const me = useAppSelector(meSelector);
  const userId = me?.id;

  React.useEffect(() => {
    if (status === "idle") {
      run(getUserExtendedAccess(userId!));
    }
  }, [run, status, userId]);

  const dataToUse = { ...data };
  delete dataToUse.extendedAccess;

  const normalizedData = Object.fromEntries(
    Object.entries(dataToUse).map(([key, value]) => {
      const resource = (
        Array.isArray(value) ? value : Object.values(value ?? {})
      ).map(resource => ({
        id: resource.id,
        value: resource.name || resource.id,
        name: resource.name || resource.title
      }));

      return [key, uniqBy(resource, "name")];
    })
  );

  return {
    data: normalizedData,
    error,
    isLoading: status === "idle" || status === "pending"
  };
}

const TypePromisesMap = {
  organizations: client.getOrganization,
  projects: client.getProject
};

export function useSelectedResource({
  type,
  id
}: {
  type: "organizations" | "projects";
  id: string;
}) {
  const { run, data, error, status } = useAsync({});

  React.useEffect(() => {
    if (status === "idle") {
      run(TypePromisesMap[type](id));
    }
  }, [id, run, status, type]);

  return {
    data,
    error,
    isLoading: status === "idle" || status === "pending"
  };
}

export function useSearchDocs(key: string) {
  const intl = useIntl();
  const { run, data, error, status } = useAsync({});
  const searchUrl = React.useMemo(
    () => new URL(intl.formatMessage({ id: "links.documentation.endpoint" })),
    [intl]
  );

  searchUrl.searchParams.set("attributesToCrop", "text");
  searchUrl.searchParams.set("cropLength", "200");
  searchUrl.searchParams.set("attributesToHighlight", "text,keywords");
  searchUrl.searchParams.set("limit", "7");
  searchUrl.searchParams.set("attributesToRetrieve", "title,text,url,section");

  React.useEffect(() => {
    if (searchUrl.searchParams.get("q") !== key) {
      searchUrl.searchParams.set("q", key);
      run(
        fetch(searchUrl, {
          headers: {
            Authorization: `Bearer 509f11463964c0434b8281def0d4d166e55d32663c63f6f0f761b0b104c6620c`
          }
        }).then(unformattedData => unformattedData.json())
      );
    }
  }, [key, run, searchUrl, status]);

  return {
    data,
    error,
    isLoading: status === "idle" || status === "pending"
  } as { data: Docs; isLoading: boolean };
}

export const CommandMenuContext = createContext<{
  isCommandMenuOpen: boolean;
  setIsCommandMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
}>({
  isCommandMenuOpen: false,
  setIsCommandMenuOpen: () => {},
  inputValue: "",
  setInputValue: () => {}
});

export function useCommandMenuContext() {
  const context = React.useContext(CommandMenuContext);
  if (context === undefined) {
    throw new Error(
      "useCommandMenuContext must be used within a CommandMenuProvider"
    );
  }
  return context;
}
